import { Marker } from 'maplibre-gl';
import { classLabel } from './poiCategories.js';
import { osmRef, osmUrl, fetchTags, fetchWikipedia, fetchGermanTitle, fetchArea } from '../data/details.js';
import { HALT_KLASSEN, haltestelleBei, abfahrten, inKuerze, linienVerlauf } from '../data/oev.js';

/**
 * Layer, die auf Klick Auskunft geben – Reihenfolge = Priorität.
 *
 * `poi-minor` muss mit: dort liegen alle Punkte mit Rang über 8 *und*
 * sämtliche Bushaltestellen (siehe POI_SPLIT im Style). Ohne diesen Eintrag
 * ist ein grosser Teil der Karte stumm.
 */
const QUERY_LAYERS = [
  'poi-symbol',
  'poi-minor',
  'label-place-city', 'label-place-town', 'label-place-small',
  'label-peak',
  'building-fill',
  'water-fill',
  'road-motorway', 'road-trunk', 'road-primary', 'road-secondary',
  'road-tertiary', 'road-minor', 'road-service', 'road-track', 'road-path',
  'lc-forest', 'lc-park'
];

const KIND = {
  'poi-symbol': 'Punkt von Interesse',
  'poi-minor': 'Punkt von Interesse',
  'label-place-city': 'Stadt',
  'label-place-town': 'Ortschaft',
  'label-place-small': 'Ortsteil',
  'label-peak': 'Gipfel',
  'building-fill': 'Gebäude',
  'water-fill': 'Gewässer',
  'lc-forest': 'Wald',
  'lc-park': 'Park'
};

const nameOf = (p = {}) => p['name:de'] || p.name_de || p.name || '';


/* ------------------------------------------------------- Einzelheiten aus OSM */

const PLACE_LAYERS = new Set(['label-place-city', 'label-place-town', 'label-place-small']);

/** Die Karte, damit auch die Hilfsfunktionen zeichnen können. */
let karte = null;
const OEV_QUELLE = 'oev-linie';
const OEV_EBENE = 'oev-linie-strich';

function linieLoeschen() {
  if (!karte) return;
  if (karte.getLayer(OEV_EBENE)) karte.removeLayer(OEV_EBENE);
  if (karte.getSource(OEV_QUELLE)) karte.removeSource(OEV_QUELLE);
}

/**
 * Verlauf einer Fahrt. Zuerst der echte Weg aus den OSM-Linienrelationen -
 * Gleis für Gleis, Strasse für Strasse. Gibt es den nicht, bleibt die Kette
 * der Haltestellen; sie ist schematisch, aber besser als nichts, und wird
 * deshalb gestrichelt gezeichnet.
 */
async function linieZeichnen(fahrt, halt) {
  if (!karte) return;
  linieLoeschen();

  const start = (fahrt.halte || []).find((h) => h.lat !== null) || null;
  const kette = (fahrt.halte || [])
    .filter((h) => h.lat !== null)
    .map((h) => [h.lng, h.lat]);

  let coords = kette;
  let genau = false;
  if (start) {
    const verlauf = await linienVerlauf({
      ref: fahrt.kuerzel, ziel: fahrt.ziel, lat: start.lat, lng: start.lng
    });
    if (verlauf) { coords = verlauf.coordinates; genau = true; }
  }
  if (coords.length < 2) return;

  karte.addSource(OEV_QUELLE, {
    type: 'geojson',
    data: { type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: {} }
  });
  karte.addLayer({
    id: OEV_EBENE,
    type: 'line',
    source: OEV_QUELLE,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#1B2460',
      'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2.5, 14, 4.5],
      'line-opacity': 0.85,
      ...(genau ? {} : { 'line-dasharray': [2, 1.5] })
    }
  });

  const lngs = coords.map((c) => c[0]);
  const lats = coords.map((c) => c[1]);
  karte.fitBounds(
    [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
    { padding: 70, duration: 800, maxZoom: 14 }
  );
}

/** Wochentage im OSM-Kürzel -> deutsch. Die Syntax bleibt sonst wie sie ist. */
const DAYS = { Mo: 'Mo', Tu: 'Di', We: 'Mi', Th: 'Do', Fr: 'Fr', Sa: 'Sa', Su: 'So', PH: 'Feiertag' };
const germanDays = (s) => s.replace(/\b(Mo|Tu|We|Th|Fr|Sa|Su|PH)\b/g, (d) => DAYS[d]);

const zahl = (n, stellen = 0) =>
  new Intl.NumberFormat('de-CH', { maximumFractionDigits: stellen }).format(n);

function row(host, label, value, href) {
  if (!value) return;
  const line = document.createElement('div');
  line.className = 'pop__row';
  const k = document.createElement('span');
  k.className = 'pop__key';
  k.textContent = label;
  const v = href ? document.createElement('a') : document.createElement('span');
  v.className = 'pop__val';
  v.textContent = value;
  if (href) { v.href = href; v.target = '_blank'; v.rel = 'noopener'; }
  line.append(k, v);
  host.append(line);
}

/** Öffnungszeiten lesbar machen: je Regel eine Zeile. */
function openingHours(host, raw) {
  if (!raw) return;
  const block = document.createElement('div');
  block.className = 'pop__hours';
  const head = document.createElement('span');
  head.className = 'pop__key';
  head.textContent = 'Öffnungszeiten';
  block.append(head);
  for (const rule of String(raw).split(';')) {
    const t = germanDays(rule.trim());
    if (!t) continue;
    const line = document.createElement('span');
    line.className = 'pop__hours-line';
    line.textContent = t;
    block.append(line);
  }
  host.append(block);
}

/** Merkmale, die oben schon als eigene Zeile erscheinen - unten nicht nochmal. */
const SCHON_GEZEIGT = new Set([
  'name', 'addr:street', 'addr:housenumber', 'addr:postcode', 'addr:city', 'addr:country',
  'opening_hours', 'phone', 'contact:phone', 'email', 'contact:email',
  'website', 'contact:website', 'url', 'operator', 'brand', 'cuisine', 'wheelchair',
  'ele', 'population', 'population:date', 'wikipedia', 'wikidata', 'source'
]);

/**
 * Der Rest, einklappbar. OSM kennt tausende Merkmale; eine feste Auswahl
 * verschweigt immer etwas. Oben steht das Übliche lesbar aufbereitet, hier
 * alles Weitere im Originalwortlaut.
 */
function alleMerkmale(host, t) {
  const rest = Object.entries(t)
    .filter(([k]) => !SCHON_GEZEIGT.has(k) && !k.startsWith('name:'))
    .sort(([a], [b]) => a.localeCompare(b));
  if (!rest.length) return;
  const box = document.createElement('details');
  box.className = 'pop__more';
  const sum = document.createElement('summary');
  sum.textContent = `Alle Merkmale (${rest.length})`;
  box.append(sum);
  for (const [k, v] of rest) {
    const line = document.createElement('div');
    line.className = 'pop__more-row';
    line.textContent = `${k} = ${v}`;
    box.append(line);
  }
  host.append(box);
}

/**
 * Abfahrtstafel an einer Haltestelle. Steht ganz oben, noch vor den Merkmalen
 * aus OSM: wer eine Haltestelle antippt, will wissen, wann der nächste kommt -
 * nicht, wer den Unterstand betreibt.
 */
async function abfahrtstafel(host, lngLat) {
  const kasten = document.createElement('div');
  kasten.className = 'pop__dep';
  kasten.textContent = 'Abfahrten …';
  host.append(kasten);

  const halt = await haltestelleBei({ lat: lngLat.lat, lng: lngLat.lng });
  if (!halt) { kasten.remove(); return; }
  abfahrtstafelFuellen(kasten, halt);
}

async function abfahrtstafelFuellen(kasten, halt) {
  kasten.textContent = 'Abfahrten …';
  let liste;
  try {
    liste = await abfahrten(halt.id, { limit: 10 });
  } catch {
    kasten.textContent = 'Fahrplan nicht erreichbar.';
    return;
  }
  // Der Fahrplan gibt nachts auch längst abgefahrene Kurse zurück. Eine
  // Tafel voller "abgefahren" hilft niemandem - dann lieber die klare
  // Auskunft, dass heute nichts mehr fährt.
  const frisch = liste.filter((a) => !a.zeit || a.zeit.getTime() > Date.now() - 30000);

  kasten.textContent = '';
  const kopf = document.createElement('div');
  kopf.className = 'pop__dep-head';
  kopf.textContent = halt.name;
  kasten.append(kopf);

  if (!frisch.length) {
    const leer = document.createElement('div');
    leer.className = 'pop__dep-row';
    leer.textContent = liste.length ? 'Zurzeit keine Abfahrten.' : 'Keine Abfahrten bekannt.';
    kasten.append(leer);
    return;
  }
  liste = frisch;

  for (const a of liste) {
    const zeile = document.createElement('div');
    zeile.className = 'pop__dep-row';

    const linie = document.createElement('span');
    linie.className = 'pop__dep-line';
    linie.textContent = a.linie;

    const ziel = document.createElement('span');
    ziel.className = 'pop__dep-to';
    ziel.textContent = a.ziel;

    const zeit = document.createElement('span');
    zeit.className = 'pop__dep-time';
    zeit.textContent = inKuerze(a.zeit);
    if (a.verspaetung) {
      const v = document.createElement('em');
      v.className = 'pop__dep-late';
      v.textContent = `+${a.verspaetung}`;
      zeit.append(' ', v);
    }
    if (a.gleis) zeile.title = `Gleis/Kante ${a.gleis}`;

    zeile.append(linie, ziel, zeit);
    zeile.tabIndex = 0;
    zeile.setAttribute('role', 'button');
    zeile.classList.add('is-klickbar');
    const oeffnen = () => fahrtZeigen(kasten, a, halt);
    zeile.addEventListener('click', oeffnen);
    zeile.addEventListener('keydown', (e) => { if (e.key === 'Enter') oeffnen(); });
    kasten.append(zeile);
  }
}

/**
 * Eine einzelne Fahrt: alle weiteren Halte mit Zeit und Verspätung, dazu der
 * Verlauf auf der Karte. Die Haltestellen stehen schon in der Abfahrtstafel -
 * dafür braucht es keine zweite Abfrage.
 */
function fahrtZeigen(kasten, fahrt, halt) {
  kasten.textContent = '';

  const zurueck = document.createElement('button');
  zurueck.type = 'button';
  zurueck.className = 'pop__dep-back';
  zurueck.textContent = '‹ Abfahrten';
  zurueck.addEventListener('click', () => {
    linieLoeschen();
    abfahrtstafelFuellen(kasten, halt);
  });

  const kopf = document.createElement('div');
  kopf.className = 'pop__dep-head';
  kopf.textContent = `${fahrt.linie} nach ${fahrt.ziel}`;
  kasten.append(zurueck, kopf);

  // Nur noch kommende Halte - was hinter einem liegt, hilft nicht.
  const jetzt = Date.now() - 60000;
  const halte = (fahrt.halte || []).filter((h) => !h.zeit || h.zeit.getTime() >= jetzt);
  if (!halte.length) {
    const leer = document.createElement('div');
    leer.className = 'pop__dep-row';
    leer.textContent = 'Keine weiteren Halte bekannt.';
    kasten.append(leer);
  }
  for (const h of halte) {
    const z = document.createElement('div');
    z.className = 'pop__dep-row pop__dep-row--stop';
    const n = document.createElement('span');
    n.className = 'pop__dep-to';
    n.textContent = h.name;
    const t = document.createElement('span');
    t.className = 'pop__dep-time';
    t.textContent = h.zeit
      ? `${String(h.zeit.getHours()).padStart(2, '0')}:${String(h.zeit.getMinutes()).padStart(2, '0')}`
      : '';
    if (h.verspaetung) {
      const v = document.createElement('em');
      v.className = 'pop__dep-late';
      v.textContent = `+${h.verspaetung}`;
      t.append(' ', v);
    }
    z.append(document.createElement('span'), n, t);
    kasten.append(z);
  }

  linieZeichnen(fahrt, halt);
}

/** Artikelauszug - bei Orten wie bei Bauwerken, die einen Eintrag haben. */
async function wikiBlock(host, t) {
  let tag = t['wikipedia:de'] || t.wikipedia;
  // Steht dort eine andere Sprache, über Wikidata die deutsche Fassung suchen.
  if (t.wikidata && !/^de:/.test(tag || '')) {
    tag = (await fetchGermanTitle(t.wikidata)) || tag;
  }
  const artikel = await fetchWikipedia(tag);
  if (!artikel?.text) return;
  const p = document.createElement('p');
  p.className = 'pop__text';
  p.textContent = artikel.text;
  host.append(p);
  row(host, '', 'Wikipedia-Artikel', artikel.url);
}

async function poiDetails(host, t) {
  const strasse = [t['addr:street'], t['addr:housenumber']].filter(Boolean).join(' ');
  const ort = [t['addr:postcode'], t['addr:city']].filter(Boolean).join(' ');
  row(host, 'Adresse', [strasse, ort].filter(Boolean).join(', '));
  openingHours(host, t.opening_hours);

  const tel = t.phone || t['contact:phone'];
  if (tel) row(host, 'Telefon', tel, `tel:${tel.replace(/[^+0-9]/g, '')}`);
  const mail = t.email || t['contact:email'];
  if (mail) row(host, 'E-Mail', mail, `mailto:${mail}`);
  const web = t.website || t['contact:website'] || t.url;
  if (web) {
    const schoen = web.replace(/^https?:\/\//, '').replace(/\/$/, '');
    row(host, 'Website', schoen.length > 38 ? `${schoen.slice(0, 38)}…` : schoen,
        /^https?:/.test(web) ? web : `https://${web}`);
  }
  row(host, 'Betreiber', t.operator || t.brand);
  row(host, 'Küche', t.cuisine && germanDays(t.cuisine).replace(/_/g, ' ').replace(/;/g, ', '));
  if (t.wheelchair) {
    row(host, 'Rollstuhl',
        { yes: 'ja', no: 'nein', limited: 'eingeschränkt' }[t.wheelchair] || t.wheelchair);
  }
  if (t.ele) row(host, 'Höhe', `${t.ele} m ü. M.`);
  await wikiBlock(host, t);
}

async function placeDetails(host, t) {
  if (t.population) {
    const datum = t['population:date'];
    const n = Number(t.population);
    row(host, 'Einwohner',
        Number.isFinite(n)
          ? `${zahl(n)}${datum ? ` (Stand ${datum})` : ''}`
          : t.population);
  }
  if (t.ele) row(host, 'Höhe', `${t.ele} m ü. M.`);
  if (t.website) {
    row(host, 'Website', t.website.replace(/^https?:\/\//, '').replace(/\/$/, ''), t.website);
  }

  const flaeche = await fetchArea(t.wikidata);
  if (flaeche) {
    row(host, 'Fläche', `${zahl(flaeche, 2)} km²`);
    const n = Number(t.population);
    if (Number.isFinite(n) && flaeche > 0) {
      row(host, 'Dichte', `${zahl(n / flaeche)} Einw./km²`);
    }
  }
  await wikiBlock(host, t);
}

/**
 * Nachladen stört den ersten Eindruck nicht: das Popup steht schon, hier
 * kommen die Einzelheiten hinterher. Schlägt der Abruf fehl, bleibt es bei
 * dem, was die Kachel wusste.
 */
async function loadDetails(host, feature, layerId) {
  // Zwei Bereiche, weil beide unabhängig voneinander nachladen: die
  // Abfahrtstafel kam sonst zurück, während das Leeren für die OSM-Merkmale
  // sie gleich wieder entfernte.
  const oben = document.createElement('div');
  const unten = document.createElement('div');
  host.append(oben, unten);

  // Haltestellen zuerst - unabhängig davon, ob OSM mehr über sie weiss.
  const klasse = feature?.properties?.class;
  if (HALT_KLASSEN.has(klasse)) {
    const g = feature.geometry;
    const [lng, lat] = g?.type === 'Point' ? g.coordinates : [null, null];
    if (lat !== null) abfahrtstafel(oben, { lat, lng });
  }

  const ref = osmRef(feature?.id);
  if (!ref) return;
  unten.textContent = 'lädt …';
  const t = await fetchTags(ref);
  unten.textContent = '';
  if (!t) return;
  const host2 = unten;

  if (PLACE_LAYERS.has(layerId)) await placeDetails(host2, t);
  else await poiDetails(host2, t);
  alleMerkmale(host2, t);

  const link = document.createElement('a');
  link.className = 'pop__osm';
  link.href = osmUrl(ref);
  link.target = '_blank';
  link.rel = 'noopener';
  link.textContent = 'In OpenStreetMap ansehen';
  host2.append(link);
}

/**
 * Die Auskunft steht neben der Karte, nicht darauf.
 *
 * Ein Fenster auf der Karte verdeckt genau die Stelle, die man gerade
 * anschaut - und auf dem Telefon beinahe alles. Deshalb rechts eine Spalte,
 * auf schmalen Geräten eine Leiste von unten. Wo geklickt wurde, bleibt ein
 * Punkt stehen, damit Karte und Text zusammengehören.
 */
export function createInfoPopup({ map, onFavorite, onRoutePoint }) {
  karte = map;

  const tafel = document.getElementById('info');
  const inhalt = document.getElementById('info-body');
  const schliessen = document.getElementById('info-close');
  let markierung = null;

  function zu() {
    if (tafel) tafel.hidden = true;
    if (inhalt) inhalt.textContent = '';
    markierung?.remove();
    markierung = null;
    linieLoeschen();
  }

  schliessen?.addEventListener('click', zu);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && tafel && !tafel.hidden) zu();
  });

  /** Steht fuer das Popup von frueher - gleiche Schnittstelle, anderer Ort. */
  const popup = {
    remove: zu,
    setLngLat() { return popup; },
    setDOMContent(knoten) {
      if (!tafel || !inhalt) return popup;
      inhalt.textContent = '';
      inhalt.append(knoten);
      tafel.hidden = false;
      inhalt.scrollTop = 0;
      return popup;
    },
    addTo() { return popup; }
  };

  function markieren(lngLat) {
    markierung?.remove();
    const punkt = document.createElement('div');
    punkt.className = 'info__pin';
    markierung = new Marker({ element: punkt, anchor: 'center' })
      .setLngLat(lngLat)
      .addTo(map);
  }

  function openAt(lngLat, feature, layerId) {
    const p = feature?.properties || {};
    const name = nameOf(p);
    const kind = KIND[layerId] || (layerId.startsWith('road-') ? 'Strasse / Weg' : 'Objekt');
    // Bei Orten ist die Klasse ("city") nur die englische Fassung der Art -
    // zweimal dasselbe in der Unterzeile hilft niemandem.
    const isPlace = PLACE_LAYERS.has(layerId);
    const sub = !isPlace && p.class ? classLabel(p.class) : kind;

    const root = document.createElement('div');
    const title = document.createElement('p');
    title.className = 'pop__title';
    title.textContent = name || sub;
    const subtitle = document.createElement('p');
    subtitle.className = 'pop__sub';
    subtitle.textContent = !name || isPlace || sub === kind ? kind : `${sub} · ${kind}`;
    root.append(title, subtitle);

    const tags = document.createElement('div');
    tags.className = 'pop__tags';
    const rows = [];
    if (p.subclass && p.subclass !== p.class) rows.push(['Art', p.subclass.replace(/_/g, ' ')]);
    if (p.ele) rows.push(['Höhe', `${p.ele} m ü. M.`]);
    if (p.housenumber) rows.push(['Nr.', p.housenumber]);
    if (p.ref) rows.push(['Nummer', p.ref]);
    rows.push(['Koordinaten', `${lngLat.lat.toFixed(5)}, ${lngLat.lng.toFixed(5)}`]);
    for (const [k, v] of rows) {
      const s = document.createElement('span');
      s.textContent = `${k}: ${v}`;
      tags.append(s);
    }
    root.append(tags);

    const details = document.createElement('div');
    details.className = 'pop__details';
    root.append(details);
    loadDetails(details, feature, layerId);

    const actions = document.createElement('div');
    actions.className = 'pop__actions';

    const favBtn = document.createElement('button');
    favBtn.type = 'button';
    favBtn.className = 'pop__btn pop__btn--fav';
    favBtn.textContent = 'Favorit';
    favBtn.addEventListener('click', async () => {
      favBtn.disabled = true;
      await onFavorite({
        name: name || sub,
        lat: lngLat.lat,
        lng: lngLat.lng,
        zoom: Math.round(map.getZoom()),
        kind: sub
      });
      popup.remove();
    });

    const copyBtn = document.createElement('button');
    copyBtn.type = 'button';
    copyBtn.className = 'pop__btn';
    copyBtn.textContent = 'Koordinaten';
    copyBtn.addEventListener('click', async () => {
      await navigator.clipboard?.writeText(`${lngLat.lat.toFixed(6)}, ${lngLat.lng.toFixed(6)}`);
      copyBtn.textContent = 'Kopiert';
    });

    const startBtn = document.createElement('button');
    startBtn.type = 'button';
    startBtn.className = 'pop__btn';
    startBtn.textContent = 'Start';
    startBtn.addEventListener('click', () => {
      onRoutePoint?.('from', { lat: lngLat.lat, lng: lngLat.lng, name: name || sub });
      popup.remove();
    });

    const zielBtn = document.createElement('button');
    zielBtn.type = 'button';
    zielBtn.className = 'pop__btn';
    zielBtn.textContent = 'Ziel';
    zielBtn.addEventListener('click', () => {
      onRoutePoint?.('to', { lat: lngLat.lat, lng: lngLat.lng, name: name || sub });
      popup.remove();
    });

    actions.append(favBtn, startBtn, zielBtn, copyBtn);
    root.append(actions);

    popup.setDOMContent(root);
    markieren(lngLat);
  }

  map.on('click', (e) => {
    const layers = QUERY_LAYERS.filter((l) => map.getLayer(l));
    const hits = map.queryRenderedFeatures(
      [[e.point.x - 6, e.point.y - 6], [e.point.x + 6, e.point.y + 6]],
      { layers }
    );
    if (!hits.length) {
      openAt(e.lngLat, null, 'point');
      return;
    }
    const best = hits.reduce((a, b) =>
      QUERY_LAYERS.indexOf(a.layer.id) <= QUERY_LAYERS.indexOf(b.layer.id) ? a : b
    );
    openAt(e.lngLat, best, best.layer.id);
  });

  /**
   * Zeiger ändern, wo ein Klick auch etwas bringt.
   *
   * Nicht über allem: Gebäude, Strassen und Wald sind zwar anklickbar, decken
   * aber die ganze Fläche ab - ein dauerhaft zeigender Finger sagt dann nichts
   * mehr aus. Hier zählen die Dinge, die man einzeln meint.
   */
  const ZEIGER_EBENEN = [
    'poi-symbol', 'poi-minor',
    'label-place-city', 'label-place-town', 'label-place-small',
    'label-peak', 'label-area-name', 'label-water', 'spot-elevation'
  ];

  const zeigerAn = () => { map.getCanvas().style.cursor = 'pointer'; };
  const zeigerAus = () => { map.getCanvas().style.cursor = ''; };

  /**
   * Nach einem Stilwechsel sind die Ebenen neu - deshalb beim Anmelden nur
   * nehmen, was es gerade gibt, und nach `styledata` nochmals anmelden.
   */
  function zeigerAnmelden() {
    const da = ZEIGER_EBENEN.filter((l) => map.getLayer(l));
    if (!da.length) return;
    map.off('mouseenter', da, zeigerAn);
    map.off('mouseleave', da, zeigerAus);
    map.on('mouseenter', da, zeigerAn);
    map.on('mouseleave', da, zeigerAus);
  }

  zeigerAnmelden();
  map.on('styledata', () => { zeigerAus(); zeigerAnmelden(); });

  return popup;
}
